import { useMemo, useState } from 'react';
import { fmtPct, fmtPrice, searchInstruments, type AssetClass, type Instrument } from '@platform/shared';
import { navigate } from '../lib/router';
import { toggleFavorite as toggle, useFavorites } from '../stores/favorites';
import { useInstruments, useTickers } from '../stores/market';
import { InstrumentIcon } from './InstrumentIcon';
import { PairName } from './PairName';
import { cx, Icon, LevBadge, MoveArrow } from './ui';

type Filter = AssetClass | 'ALL' | 'FAV';

const FILTERS: { id: AssetClass | 'ALL'; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'CRYPTO', label: 'Crypto' },
  { id: 'STOCK', label: 'Stocks' },
  { id: 'COMMODITY', label: 'Commodities' },
  { id: 'INDEX', label: 'Indices' },
  { id: 'FX', label: 'Forex' },
];

/** Markets each class shows in the grouped "All" view before "See all". */
const PER_GROUP = 5;

const chip = (on: boolean) => cx('inline-flex h-[26px] items-center gap-1 rounded-full border px-2.5 text-[11.5px] font-medium', on ? 'border-line-strong bg-raised text-fg' : 'border-line text-muted hover:text-fg');

/** Instrument watchlist on the trade page: search, class filter, favourites. "All" is grouped by asset class. */
export function AssetList({ current, onPick, onHide }: { current: string; onPick?: () => void; onHide?: () => void }) {
  const instruments = useInstruments();
  const tickers = useTickers();
  const favs = useFavorites();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('ALL');

  const rows = useMemo(() => {
    const inFilter = (i: Instrument) => filter === 'ALL' || (filter === 'FAV' ? favs.includes(i.symbol) : i.assetClass === filter);
    if (!query.trim()) return instruments.filter(inFilter);
    return searchInstruments(instruments, query, (i) => Number(tickers[i.symbol]?.vol24) || 0)
      .map((h) => h.item)
      .filter(inFilter);
  }, [instruments, query, filter, favs, tickers]);

  const grouped = filter === 'ALL' && !query.trim();

  const row = (i: Instrument) => {
    const t = tickers[i.symbol];
    const fav = favs.includes(i.symbol);
    const active = i.symbol === current;
    const closed = t && !t.open;
    const neg = !!t?.chg24?.startsWith('-');
    return (
      <div key={i.symbol} className={cx('grid grid-cols-[28px_1fr] items-center border-b border-line pl-1.5 hover:bg-raised', active && 'bg-raised shadow-[inset_2px_0_0_var(--accent)]')}>
        <button type="button" onClick={() => toggle(i.symbol)} aria-pressed={fav} aria-label={`Favourite ${i.symbol}`} className={cx('grid h-[52px] w-7 place-items-center', fav ? 'text-warn' : 'text-line-strong hover:text-muted')}>
          <Icon name="star" size={14} className={fav ? 'fill-warn' : undefined} />
        </button>
        <button
          type="button"
          onClick={() => {
            navigate(`/trade/${i.symbol}`);
            onPick?.();
          }}
          aria-current={active ? 'page' : undefined}
          className="flex h-[52px] min-w-0 items-center justify-between gap-2 pr-3 text-left"
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <InstrumentIcon symbol={i.symbol} size={28} />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className={cx('flex min-w-0 items-center gap-1.5 text-[13px] font-semibold', closed && 'opacity-70')}>
                <PairName symbol={i.symbol} assetClass={i.assetClass} />
                <LevBadge lev={i.maxLeverage} />
              </span>
              <span className="flex min-w-0 items-center gap-1 text-[11px] text-muted">
                <span className="truncate">
                  {i.name}
                  {i.assetClass === 'COMMODITY' && i.unit ? ` · per ${i.unit}` : ''}
                </span>
                {closed && (
                  <span className="shrink-0 rounded border border-line-strong px-1 text-[9.5px] leading-[14px] font-medium" title="Market closed">
                    Closed
                  </span>
                )}
                {t?.paused && (
                  <span className="shrink-0 text-warn" title="No live price feed: this price is indicative">
                    · indicative
                  </span>
                )}
              </span>
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end gap-0.5">
            <span className={cx('num text-[12.5px]', closed && 'text-muted', t?.paused && 'text-warn')}>{t ? fmtPrice(t.mark, i.priceDecimals, i.priceCcy) : '—'}</span>
            {t ? (
              <span className={cx('num inline-flex items-center gap-0.5 rounded px-1.5 text-[11px] font-medium', neg ? 'bg-down-bg text-down' : 'bg-up-bg text-up', closed && 'opacity-70')}>
                <MoveArrow fall={neg} size={10} />
                {fmtPct(t.chg24)}
              </span>
            ) : (
              <span className="text-[11px] text-muted">—</span>
            )}
          </span>
        </button>
      </div>
    );
  };

  return (
    <aside aria-label="Assets" className="flex h-full min-h-0 flex-col bg-panel">
      <div className="flex flex-col gap-2.5 border-b border-line p-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Assets</span>
          {onHide && (
            <button type="button" onClick={onHide} aria-label="Hide assets" title="Hide assets" className="grid size-7 place-items-center rounded-md text-muted hover:bg-raised hover:text-fg">
              <Icon name="right" size={16} className="rotate-180" />
            </button>
          )}
        </div>
        <label className="flex h-[34px] items-center gap-2 rounded-lg border border-line-strong bg-bg px-2.5">
          <Icon name="search" size={14} className="text-muted" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search assets" aria-label="Search assets" className="min-w-0 flex-1 bg-transparent text-fg outline-none placeholder:text-muted" />
        </label>
        <div role="group" aria-label="Asset class" className="flex flex-wrap gap-1">
          <button type="button" aria-pressed={filter === 'FAV'} aria-label="Favourites" onClick={() => setFilter('FAV')} className={chip(filter === 'FAV')}>
            <Icon name="star" size={12} />
          </button>
          {FILTERS.map((f) => (
            <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)} className={chip(filter === f.id)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {grouped
          ? FILTERS.slice(1).map((f) => {
              const group = rows.filter((i) => i.assetClass === f.id);
              if (!group.length) return null;
              return (
                <section key={f.id} aria-label={f.label}>
                  <div className="sticky top-0 z-[1] flex items-center justify-between border-b border-line bg-panel px-3 py-1.5 font-mono text-[10.5px] font-medium tracking-[0.07em] text-muted uppercase">
                    <span>
                      {f.label} · {group.length}
                    </span>
                    {group.length > PER_GROUP && (
                      <button type="button" onClick={() => setFilter(f.id)} className="bg-transparent font-sans text-[11px] tracking-normal text-accent-fg normal-case hover:underline">
                        See all
                      </button>
                    )}
                  </div>
                  {group.slice(0, PER_GROUP).map(row)}
                </section>
              );
            })
          : rows.map(row)}
        {!rows.length && <div className="px-3 py-8 text-center text-muted">{filter === 'FAV' && !query ? 'Star an asset to add it here' : 'No assets match'}</div>}
      </div>
      <div className="flex justify-between border-t border-line px-3 py-2 text-[11px] text-muted">
        <span>{instruments.length} markets</span>
        <span className="flex items-center gap-1">
          <kbd className="num rounded border border-line-strong px-1 text-[10px]">/</kbd> search all
        </span>
      </div>
    </aside>
  );
}

/** What's left of the watchlist when the trader hides it: a narrow strip that brings it back. */
export function AssetRail({ onShow }: { onShow: () => void }) {
  return (
    <div className="flex h-full w-11 flex-col items-center gap-3 bg-panel py-3">
      <button type="button" onClick={onShow} aria-label="Show assets" title="Show assets" className="grid size-7 place-items-center rounded-md text-muted hover:bg-raised hover:text-fg">
        <Icon name="right" size={16} />
      </button>
      <span className="rotate-180 text-[11.5px] font-semibold text-muted [writing-mode:vertical-rl]">Assets</span>
    </div>
  );
}

const HIDDEN_KEY = 'assetsHidden';
/** Whether the trader hid the watchlist, remembered on this device. */
export function useAssetsHidden() {
  const [hidden, setHiddenState] = useState(() => {
    try {
      return localStorage.getItem(HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setHidden = (h: boolean) => {
    setHiddenState(h);
    try {
      localStorage.setItem(HIDDEN_KEY, h ? '1' : '0');
    } catch {
      /* storage unavailable: the choice lasts for this visit */
    }
  };
  return [hidden, setHidden] as const;
}
